/**
 * Utility functions for the Media Buying Report
 */

/**
 * Format a number with specified decimal places
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString(undefined, { 
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

/**
 * Format a number as currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `$${formatNumber(amount, 0)}`;
};

/**
 * Calculate Click-Through Rate
 * @param {number} clicks - Number of clicks
 * @param {number} impressions - Number of impressions
 * @returns {number} CTR as a percentage
 */
export const calculateCTR = (clicks, impressions) => {
    if (!impressions || impressions === 0) return 0;
    return (clicks / impressions) * 100;
};

/**
 * Calculate Cost Per Click
 * @param {number} cost - Total cost
 * @param {number} clicks - Number of clicks
 * @returns {number} CPC
 */
export const calculateCPC = (cost, clicks) => {
    if (!clicks || clicks === 0) return 0;
    return cost / clicks;
};

/**
 * Calculate Return On Investment
 * @param {number} revenue - Total revenue
 * @param {number} cost - Total cost
 * @returns {number} ROI as a percentage
 */
export const calculateROI = (revenue, cost) => {
    if (!cost || cost === 0) return 0;
    return ((revenue - cost) / cost) * 100;
};

/**
 * Calculate ROAS (Return on Ad Spend) consistently
 * @param {Object} report - Campaign report data
 * @param {string} context - Optional context for debugging
 * @returns {number} Calculated ROAS value
 */
export const calculateRoas = (report, context = 'default') => {
    if (!report) return 0;
    
    // Always use revenue and cost for consistent ROAS calculation
    const revenue = parseFloat(report.revenue) || 0;
    const cost = parseFloat(report.cost) || 0;
    
    // Calculate ROAS
    const roas = cost > 0 ? revenue / cost : 0;
    
    // Debug log for specific campaigns
    if (report.sub_id === '44123876') {
        debugRoasCalculation(report, context);
    }
    
    // Avoid division by zero
    return roas;
};

/**
 * Check if a campaign has low performance
 * @param {Object} report - Campaign report data
 * @param {string} context - Optional context for debugging
 * @returns {boolean} True if campaign has low performance
 */
export const hasLowPerformance = (report, context = 'unknown') => {
    if (!report) {
        console.warn('hasLowPerformance called with null or undefined report');
        return false;
    }
    
    try {
        // Special debug for campaign 44123876
        if (report.sub_id === '44123876') {
            console.debug('Checking low performance for campaign 44123876:', {
                rawReport: report,
                revenue: parseFloat(report.revenue) || 0,
                cost: parseFloat(report.cost) || 0
            });
        }
        
        // Special debug for campaign 44247723
        if (report.sub_id === '44247723') {
            console.debug('Checking low performance for campaign 44247723:', {
                rawReport: report,
                revenue: parseFloat(report.revenue) || 0,
                cost: parseFloat(report.cost) || 0,
                clicks: parseFloat(report.clicks) || 0,
                total_revenue: parseFloat(report.total_revenue) || 0,
                cvr: parseFloat(report.cvr) || 0
            });
        }
        
        // Convert string values to numbers for comparison
        const clicks = parseFloat(report.clicks) || 0;
        const total_revenue = parseFloat(report.total_revenue) || 0;
        
        // If there are no clicks, we can't properly evaluate performance
        // Don't mark campaigns with no clicks as low performance
        if (clicks === 0) {
            if (Math.random() < 0.005) {
                console.debug(`LowPerf Analysis (NO): Campaign ${report.sub_id} has no clicks, skipping low performance check`);
            }
            return false;
        }
        
        // Calculate payout directly from total_revenue and clicks
        const payout = clicks > 0 ? total_revenue / clicks : 0;
        
        const cvr = parseFloat(report.cvr) || 0;
        // Use the unified ROAS calculation
        const roas = calculateRoas(report, 'hasLowPerformance');
        const conversions = parseFloat(report.conversions) || 0;
        const taboola_clicks = parseFloat(report.taboola_clicks) || 0;
        
        // Use the appropriate clicks field
        const actualClicks = clicks > 0 ? clicks : taboola_clicks;
        
        // Note: We're no longer considering "unrealistic values" for low performance classification
        // We'll still compute it for reference/debugging, but won't use it for classification
        const hasUnrealisticValues = 
            (cvr > 100) || // CVR shouldn't be over 100%
            (conversions > actualClicks && actualClicks > 0) || // Can't have more conversions than clicks
            (roas > 100); // Extremely high ROAS might indicate data issues
            
        // Check for low performance metrics
        const hasLowPayout = payout < 0.095; // Slightly lower threshold to avoid edge cases
        const hasLowCVR = cvr < 10;
        const hasLowROAS = roas < 0.20;
        const hasLowCoverage = parseFloat(report.coverage) < 80; // Coverage should be above 80%
        
        // IMPORTANT: Only consider the main criteria for low performance
        // Removed hasUnrealisticValues from this calculation per user requirement
        const isLowPerformance = hasLowPayout || hasLowCVR || hasLowROAS || hasLowCoverage;
        
        // Special debug for campaign 44123876
        if (report.sub_id === '44123876') {
            console.debug('Low performance analysis for campaign 44123876:', {
                payout,
                cvr,
                roas,
                coverage: parseFloat(report.coverage) || 0,
                hasLowPayout,
                hasLowCVR,
                hasLowROAS,
                hasLowCoverage,
                isLowPerformance
            });
        }
        
        // Special debug for campaign 44247723
        if (report.sub_id === '44247723') {
            console.debug('Low performance analysis for campaign 44247723:', {
                payout,
                cvr,
                roas,
                hasLowPayout,
                hasLowCVR,
                hasLowROAS,
                isLowPerformance,
                context,
                date: report.date,
                isAggregated: report.reports ? true : false,
                reportsCount: report.reports ? report.reports.length : 0,
                // Add more detailed metrics
                clicks,
                total_revenue,
                revenue: parseFloat(report.revenue) || 0,
                cost: parseFloat(report.cost) || 0,
                // Add thresholds for comparison
                payoutThreshold: 0.095,
                cvrThreshold: 10,
                roasThreshold: 0.20
            });
        }
        
        // Existing debugging code
        if (Math.random() < 0.005 || report.sub_id === 'debug_campaign') { 
            const reasons = [];
            if (hasLowPayout) reasons.push(`Low payout: $${payout.toFixed(2)} < $0.095`);
            if (hasLowCVR) reasons.push(`Low CVR: ${cvr.toFixed(2)}% < 10%`);
            if (hasLowROAS) reasons.push(`Low ROAS: ${roas.toFixed(2)} < 0.20`);
            
            console.debug(`LowPerf Analysis (${isLowPerformance ? 'YES' : 'NO'}): Campaign ${report.sub_id}`, {
                metrics: {
                    payout: `$${payout.toFixed(2)}`,
                    cvr: `${cvr.toFixed(2)}%`,
                    roas: roas.toFixed(2),
                    conversions,
                    clicks: actualClicks
                },
                isLowPerformance,
                reasons: reasons.length > 0 ? reasons : ['Performance is good']
            });
        }
        
        return isLowPerformance;
    } catch (error) {
        console.error('Error in hasLowPerformance:', error, report);
        return false;
    }
};

/**
 * Process data for all campaigns by grouping by date
 * @param {Array} data - Raw campaign data
 * @returns {Array} Processed data grouped by date
 */
export const processDataForAllCampaigns = (data) => {
    // Group data by date
    const groupedByDate = data.reduce((acc, item) => {
        const date = item.date;
        if (!acc[date]) {
            acc[date] = {
                date,
                // Raw values for calculations
                cost: 0,
                clicks: 0,
                conversions: 0,
                taboola_clicks: 0,
                revenue: 0,
                total_revenue: 0,
                searches: 0,
                hits: 0,
                avg_tq_sum: 0,
                tq_sum: 0,  // Add tq_sum field for direct calculation
                tq_count: 0,
                count: 0
            };
        }
        
        // Sum up raw values for later calculations
        acc[date].cost += parseFloat(item.cost) || 0;
        acc[date].clicks += parseFloat(item.clicks) || 0;
        acc[date].conversions += parseFloat(item.conversions) || 0;
        acc[date].taboola_clicks += parseFloat(item.taboola_clicks) || 0;
        acc[date].revenue += parseFloat(item.revenue) || 0;
        acc[date].total_revenue += parseFloat(item.total_revenue) || 0;
        
        // Sum up searches and hits for coverage calculation
        acc[date].searches += parseFloat(item.searches) || 0;
        acc[date].hits += parseFloat(item.hits) || 0;
        
        // Add two different approaches for TQ calculation
        // 1. Using avg_tq if available (legacy approach)
        const itemAvgTq = parseFloat(item.avg_tq) || parseFloat(item.tq) || 0;
        if (itemAvgTq > 0) {
            acc[date].avg_tq_sum += itemAvgTq;
            acc[date].tq_count += 1;
        }
        
        // 2. Using raw tq_sum and tq_count for direct calculation (preferred approach)
        const itemTqSum = parseFloat(item.tq_sum) || 0;
        const itemTqCount = parseFloat(item.tq_count) || 0;
        
        if (itemTqCount > 0) {
            acc[date].tq_sum += itemTqSum;
            acc[date].tq_count += itemTqCount;
        }
        
        acc[date].count += 1;
        
        return acc;
    }, {});
    
    // Convert grouped data to array and calculate derived metrics
    return Object.values(groupedByDate).map(item => {
        const cpc = item.taboola_clicks > 0 ? item.cost / item.taboola_clicks : 0;
        const payout = item.clicks > 0 ? item.total_revenue / item.clicks : 0;
        const cvr = item.taboola_clicks > 0 ? (item.conversions / item.taboola_clicks) * 100 : 0;
        const roas = item.cost > 0 ? item.revenue / item.cost : 0;
        const profit = item.total_revenue - item.cost;
        const coverage = item.hits > 0 ? (item.searches / item.hits) * 100 : 0;
        
        // Calculate avg_tq using direct tq_sum and tq_count if available
        let avg_tq = 0;
        
        if (item.tq_count > 0) {
            // First try to calculate using the raw tq_sum/tq_count (preferred)
            if (item.tq_sum > 0) {
                avg_tq = Math.round(item.tq_sum / item.tq_count);
            } 
            // Fall back to avg_tq_sum if needed
            else if (item.avg_tq_sum > 0) {
                avg_tq = Math.round(item.avg_tq_sum / item.tq_count);
            }
        }
        
        return {
            ...item,
            cpc,
            payout,
            cvr,
            roas,
            profit,
            coverage,
            avg_tq,
            tq_status: avg_tq > 0 ? 'valid' : (item.tq_count > 0 ? 'zero' : 'missing')
        };
    }).sort((a, b) => {
        // Sort by date (chronologically)
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
    });
};

/**
 * Parse dates in various formats
 * @param {string|Date} dateStr - Date string or object
 * @returns {Date|null} Parsed date or null if invalid
 */
export const parseDate = (dateStr) => {
    if (!dateStr) return null;
    
    // If it's already a Date object
    if (dateStr instanceof Date) return dateStr;
    
    // Try different formats
    let parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) return parsedDate;
    
    // If all parsing attempts fail
    console.error('Could not parse date:', dateStr);
    return null;
};

/**
 * Calculate totals from reports
 * @param {Array} reports - Campaign reports
 * @returns {Object} Calculated totals
 */
export const calculateTotals = (reports) => {
    if (!reports || reports.length === 0) {
        return {
            conversions: 0,
            revenue: 0,
            total_revenue: 0,
            clicks: 0,
            payout: 0,
            cost: 0,
            profit: 0,
            roas: 0
        };
    }
    
    // Initialize totals object
    const totals = {
        conversions: 0,
        revenue: 0,
        total_revenue: 0,
        clicks: 0,
        payout: 0,
        cost: 0,
        profit: 0,
        roas: 0
    };
    
    // Sum up metrics from all reports
    reports.forEach(report => {
        totals.conversions += parseFloat(report.conversions) || 0;
        totals.revenue += parseFloat(report.revenue) || 0;
        totals.total_revenue += parseFloat(report.total_revenue) || 0;
        totals.clicks += parseFloat(report.clicks) || 0;
        
        // Add cost - directly use the cost field
        const cost = parseFloat(report.cost);
        if (!isNaN(cost)) {
            totals.cost += cost;
        }
    });
    
    // Calculate payout
    totals.payout = totals.clicks > 0 ? totals.total_revenue / totals.clicks : 0;
    
    // Calculate profit
    totals.profit = totals.total_revenue - totals.cost;
    
    // Calculate ROAS using the unified method
    // For totals, we need to calculate it from the aggregated values
    totals.roas = totals.cost > 0 ? totals.revenue / totals.cost : 0;
    
    // Log the calculated totals for debugging
    console.debug('Calculated totals:', {
        ...totals,
        reportCount: reports.length,
        payoutCalculation: 'Using total_revenue / clicks for payout, but revenue for other metrics',
        profitCalculation: `${totals.total_revenue} - ${totals.cost} = ${totals.profit}`,
        roasCalculation: `${totals.revenue} / ${totals.cost} = ${totals.roas}`
    });
    
    return totals;
};

/**
 * Calculate percentage change between current and previous values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} Percentage change (positive for increase, negative for decrease)
 */
export const calculateTrend = (current, previous) => {
    if (previous === 0) return 0; // Avoid division by zero
    return ((current - previous) / previous) * 100;
};

/**
 * Calculate trends for all metrics by comparing current and previous periods
 * @param {Object} currentTotals - Totals for current period
 * @param {Object} previousTotals - Totals for previous period
 * @returns {Object} Trends for all metrics
 */
export const calculateMetricTrends = (currentTotals, previousTotals) => {
    if (!currentTotals || !previousTotals) {
        return {
            healthTrend: 0,
            campaignsTrend: 0,
            costTrend: 0,
            payoutTrend: 0,
            cvrTrend: 0,
            roasTrend: 0
        };
    }

    return {
        costTrend: calculateTrend(currentTotals.cost, previousTotals.cost),
        payoutTrend: calculateTrend(currentTotals.payout, previousTotals.payout),
        cvrTrend: calculateTrend(
            calculateCTR(currentTotals.conversions, currentTotals.clicks),
            calculateCTR(previousTotals.conversions, previousTotals.clicks)
        ),
        roasTrend: calculateTrend(currentTotals.roas, previousTotals.roas)
    };
};

/**
 * Debug function to log ROAS calculation details for a specific campaign
 * @param {Object} report - Campaign report data
 * @param {string} context - Context where the calculation is happening
 */
export const debugRoasCalculation = (report, context = 'unknown') => {
    if (!report || !report.sub_id) return;
    
    // Only debug specific campaigns or randomly sample
    if (report.sub_id === '44123876' || Math.random() < 0.01) {
        const revenue = parseFloat(report.revenue) || 0;
        const cost = parseFloat(report.cost) || 0;
        const calculatedRoas = cost > 0 ? revenue / cost : 0;
        const storedRoas = parseFloat(report.roas) || 0;
        
        console.debug(`ROAS Calculation [${context}] for campaign ${report.sub_id}:`, {
            revenue,
            cost,
            calculatedRoas,
            storedRoas,
            difference: Math.abs(calculatedRoas - storedRoas),
            percentDifference: storedRoas > 0 ? ((calculatedRoas - storedRoas) / storedRoas) * 100 : 'N/A',
            context,
            date: report.date,
            dateRange: report.dateRange
        });
    }
}; 